<template>
	<div>
		<div class="title-container">
			<div class="title">{{ module.singular_name }} Files</div>
			<div class="buttons">
				<a href @click.prevent="$router.go(-1)" class="button"><div class="hover-title">Go Back</div><span class="fa fa-chevron-left"></span></a>
			</div>
		</div>

		<file-uploader :upload-url="`/module/${ slug }/file-gallery/${ rowId }/upload`" @file-added="fileAdded"></file-uploader>

		<div class="grid two">
			<div class="conjoin-input-containers" v-for="file in files" :key="`file-${ file.id }`" v-sortable="{ onUpdate: reorderFiles, handle: '.input-dragger', filter: '.no-drag, .edit', preventOnFilter: false, chosenClass: 'dragging', animation: 150, scroll: true }">
				<div class="input-container">
					<div class="input-title"><span class="fa fa-file"></span>File Title</div>
					<div v-if="isImage(file)" class="input-photo"><a :href="`${ file.path }${ file.filename }`" target="_blank"><img :src="`${ file.path }${ file.filename }`"><div class="hover-title"><span class="fa fa-search"></span></div></a></div>
					<div class="input-box"><input type="text" :ref="`original-filename-${ file.id }`" v-model="editing[ file.id ].original_filename" @focus="editFile(file)"></div>
					<div class="input-link">
						<a v-if="isEditing(file)" href @click.prevent="updateFile(editing[file.id])" class="button" tabindex="-1"><div class="hover-title">Save</div><span class="fa fa-save"></span></a>
						<a v-if="isEditing(file)" href @click.prevent="cancelEditing(file)" class="button" tabindex="-1"><div class="hover-title">Cancel</div><span class="fa fa-undo"></span></a>

						<a v-if="isConfirmingDelete(file)" href @click.prevent="deleteFile(file)" class="button" tabindex="-1"><div class="hover-title">Confirm</div><span class="fa fa-trash"></span></a>
						<a v-if="isConfirmingDelete(file)" href @click.prevent="confirmingDelete = null" class="button" tabindex="-1"><div class="hover-title">Cancel</div><span class="fa fa-undo"></span></a>

						<a v-if="!isEditing(file) && !isConfirmingDelete(file)" :href="file.path + file.filename" :download="file.original_filename" target="_blank" class="button" tabindex="-1"><div class="hover-title">Download File</div><span class="fa fa-cloud-download"></span></a>
						<a href v-if="!isEditing(file) && !isConfirmingDelete(file)" @click.prevent="confirmingDelete = file.id" class="button" tabindex="-1"><div class="hover-title">Delete</div><span class="fa fa-trash"></span></a>
						<span v-if="files.length > 1 && !isEditing(file) && !isConfirmingDelete(file)" class="button input-dragger"><div class="hover-title">Drag To Sort</div><span class="fa fa-arrows-alt"></span></span>
					</div>
				</div>
				<div class="input-container">
					<div class="input-box"><textarea-autosize :ref="`caption-${ file.id }`" v-model="editing[ file.id ].caption" placeholder="Type caption here" @focus.native="editFile(file)"></textarea-autosize></div>
				</div>
			</div>
		</div>
	</div>
</template>

<style>

	@keyframes shake {
		0% {
			transform: rotate(-4deg)
		}
		to {
			transform: rotate(4deg)
		}
	}

	.sortable-ghost {
		opacity: 0.7 !important;
		/*animation-name: shake;*/
		/*animation-duration: .47s;*/
		/*animation-iteration-count: infinite;*/
		/*animation-direction: alternate;*/
		/*transition: all .3s;*/
	}

	.deleting {
		opacity: 0.35 !important;
	}
</style>

<script>

import _ from 'underscore';
import FileUploader from '@/views/components/FileUploaderComponent';
import RowTemplateBuilder from '@/utils/RowTemplateBuilder';

export default {

	name: 'FileGalleryPage',

	props: ['slug', 'rowId'],

	async asyncData({ app, route }) {
		const { data } = await app.$api.get(`/module/${route.params.slug}/file-gallery/${route.params.rowId}/files`);

		const editing = {};
		data.files.forEach(file => {
			editing[file.id] = { ...file, editing: false };
		});

		return {
			loadedData: data,
			editing,
		};
	},

	data() {
		return {
			loadedData: {},
			module: {},
			row: {},
			files: [],
			editing: {},
			confirmingDelete: null,
			galleryTitle: '',
		};
	},

	created() {
		this.setData(this.loadedData);
		this.loadedData = {};
	},

	methods: {

		async loadData() {
			const payload = await this.$api.get(`/module/${this.slug}/file-gallery/${this.rowId}/files`);
			this.setData(payload.data);
		},

		fileAdded(file) {
			this.files.unshift(file);
			this.$set(this.editing, file.id, { ...file, editing: false, deleting: false });

			this.loadData();
		},

		setData({ module, row, files, boilerplate }) {
			this.module = module || {};
			this.row = row || {};
			this.files = files || [];

			this.galleryTitle = RowTemplateBuilder.moduleRowValue(module, row);
			boilerplate.page.subtitle = this.galleryTitle;

			this.$store.commit('setHeaderButtons', [{ title: 'Go Back', icon: 'fa fa-chevron-left', link: `${ module.url_path_prefix }${ this.slug }` }]);
			this.$store.commit('setPageData', boilerplate.page);
			this.$store.commit('setHeaderTags', []);
		},

		isEditing(file) {
			return this.editing[ file.id ].editing;
		},

		isImage(file) {
			const photoExtensions = [ 'jpg', 'jpeg', 'png', 'bmp', 'gif' ];
			const filename = (file.filename || '').toLowerCase();
			console.log('isImage?', photoExtensions.includes(filename), filename);
			return photoExtensions.includes(filename.split('.').pop());
		},

		isConfirmingDelete(file) {
			return file.id === this.confirmingDelete;
		},

		isDeleting(file) {
			return file.pendingDeletion === true;
		},

		reorderFiles(event) {
			this.files.splice(event.newIndex, 0, this.files.splice(event.oldIndex, 1)[ 0 ]);
			this.saveSort();
		},

		saveSort() {
			const order = this.files.map((file, index) => {
				return { id: file.id, order: index };
			});

			this.$api.post(`/module/${this.module.slug}/file-gallery/${this.row.id}/order`, { order }).then(() => {
				this.$store.dispatch('addToast', 'Sort Position Saved');
			});
		},

		editFile(file) {
			this.$set(this.editing[file.id], 'editing', true);
		},

		cancelEditing(file) {
			this.$set(this.editing[file.id], 'editing', false);
			let index = _.findIndex(this.files, { id: file.id });
			if (index !== -1) {
				if (index !== -1) {
					this.$set(this.editing, file.id, {
						...this.editing[ file.id ],
						...this.files[ index ],
						editing: false,
					});
				}
			}
		},

		updateFile(file) {

			let index = _.findIndex(this.files, { id: file.id });

			if (index === -1) {
				return this.$store.dispatch('errorToast', 'An issue occurred and we were not able to update your file.');
			}

			// TODO API Call
			this.$api.post(`/module/${this.slug}/file-gallery/${this.rowId}/files/${file.id}/update`, file).then(() => {
				this.$store.dispatch('addToast', 'File Updated');
			});

			this.$set(this.files, index, {
				...this.files[ index ],
				...this.editing[ file.id ],
			});

			this.cancelEditing(file);
		},

		// /{slug}/file-gallery/{module_row_id}/files/{file_id}/update
		// /{slug}/file-gallery/{module_row_id}/files/{file_id}/delete

		async deleteFile(file) {
			file.pendingDeletion = true;
			this.$forceUpdate();

			try {
				await this.$api.post(`/module/${this.slug}/file-gallery/${this.rowId}/files/${file.id}/delete`);
				this.$store.dispatch('addToast', 'File Deleted');

				let index = _.findIndex(this.files, { id: file.id });

				if (index !== -1) {
					this.$delete(this.files, index);
					this.$forceUpdate();
				}
			} catch(e) {
				this.$store.dispatch('errorToast', 'There was a problem deleting that file');
				file.pendingDeletion = false;
				this.$forceUpdate();
			}
		},

	},

	components: {
		FileUploader,
	},

};
</script>
